import { supabase } from "@/lib/supabase";
import type { MessageThread, ThreadMessage } from "@/types";
import { useCallback, useEffect, useState } from "react";

export type MessageThreadWithMessages = MessageThread & {
	thread_messages: ThreadMessage[];
};

export function useMessageThread(threadId: string | null | undefined) {
	const [thread, setThread] = useState<MessageThreadWithMessages | null>(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const fetchThread = useCallback(async () => {
		if (!threadId) {
			setThread(null);
			setError(null);
			return;
		}

		setLoading(true);
		setError(null);

		const { data, error: threadError } = await supabase
			.from("message_threads")
			.select("*, thread_messages(*)")
			.eq("id", threadId)
			.single();

		if (threadError) {
			setError(threadError.message);
			setThread(null);
			setLoading(false);
			return;
		}

		const typedThread = data as MessageThreadWithMessages;
		setThread({
			...typedThread,
			thread_messages: [...(typedThread.thread_messages ?? [])].sort((a, b) => {
				const aTime = new Date(a.sent_at ?? a.created_at).getTime();
				const bTime = new Date(b.sent_at ?? b.created_at).getTime();
				return aTime - bTime;
			}),
		});
		setLoading(false);
	}, [threadId]);

	useEffect(() => {
		void fetchThread();
	}, [fetchThread]);

	return { thread, loading, error, refetch: fetchThread };
}
